import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Select, TableBody, SearchActionBar } from '@insurancemenu/imenu-components-lib';
import { ProductTemplateSortBy, ProductTemplateSortOrder } from 'types/productServiceV2';
import { getEffectiveYears } from 'global/utils/getEffectiveYears';
import { useShelfRFQProductTemplatesStore } from 'store/pages/rfq/rfq-template/shelf-rfq-product-templates/pageStore';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import {
  ContentStyled,
  FilterBarStyled,
  FilterSectionStyled,
  HeadingsRowStyled,
  TableHeaderStyled,
  TableHeadingStyled,
  TableWrapperStyled,
  TemplateTableStyled,
} from './styles';
import TableRow from './TableRow';
import {
  SHELF_PRODUCT_TEMPLATES_DELETE_TEMPLATE_MODAL_TEST_IDS,
  SHELF_PRODUCT_TEMPLATES_FILTERS_TEST_IDS,
  SHELF_PRODUCT_TEMPLATES_TABLE_TEST_IDS,
} from '../constants';
import UploadJsonModal from '../UploadJsonModal';

const ShelfRFQProductTemplatesTable: React.FC = observer(() => {
  const {
    isLoading,
    searchProductName,
    productLineName,
    shelfProductTemplates,
    totalElements,
    page,
    totalPages,
    sortBy,
    sortOrder,
    effectiveYear,
    uploadJsonModalDelegate,
    deleteTemplateModalDelegate,
    getInitialData,
    getShelfProductTemplates,
    setPage,
    setTotalPages,
    setSearchProductName,
    setProductLineName,
    setSortBy,
    setSortOrder,
    setEffectiveYear,
    resetInitialData,
  } = useShelfRFQProductTemplatesStore();

  useEffect(() => {
    void getInitialData();
    return resetInitialData;
  }, []);

  useEffect(() => {
    void getShelfProductTemplates();
  }, [page]);

  useEffect(() => {
    setPage(1);
    setTotalPages(1);
    void getShelfProductTemplates();
  }, [productLineName, searchProductName, sortBy, sortOrder, effectiveYear]);

  // Actions
  const onSearch = (value: string): void => {
    setSearchProductName(value);
    setPage(1);
  };

  const onReset = (): void => {
    setSearchProductName('');
    setPage(1);
  };

  const onTableSort = (sortKey: string, sortingOrder: 'ASC' | 'DESC'): void => {
    setSortBy(sortKey as ProductTemplateSortBy);
    setSortOrder(sortingOrder as ProductTemplateSortOrder);
  };

  const onUploadJson = async (): Promise<void> => {
    await uploadJsonModalDelegate.uploadFile();
    uploadJsonModalDelegate.close();
    void getShelfProductTemplates();
  };
  const onDeleteTemplate = async (): Promise<void> => {
    await deleteTemplateModalDelegate.deleteProductTemplate();
    deleteTemplateModalDelegate.close();
    void getShelfProductTemplates();
  };

  return (
    <ContentStyled>
      <FilterBarStyled>
        <FilterSectionStyled>
          <Select
            label="Product Line"
            name="productLine"
            id="productLine"
            value={productLineName}
            options={[
              { value: '', label: 'All' },
              {
                value: 'Dental',
                label: 'Dental',
              },
              {
                value: 'Vision',
                label: 'Vision',
              },
            ]}
            placeholder="All"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setProductLineName(e.target.value);
            }}
            data-test-id={SHELF_PRODUCT_TEMPLATES_FILTERS_TEST_IDS.PL_SELECT}
          />
          <Select
            label="Effective Year"
            name="effectiveYear"
            id="effectiveYear"
            value={effectiveYear}
            options={getEffectiveYears()}
            placeholder="Effective Year"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setEffectiveYear(e.target.value);
            }}
            data-test-id={SHELF_PRODUCT_TEMPLATES_FILTERS_TEST_IDS.EFFECTIVE_YEAR_SELECT}
          />
        </FilterSectionStyled>
      </FilterBarStyled>
      <TableWrapperStyled>
        <SearchActionBar
          totalElements={totalElements}
          totalTitle="Total Products"
          searchPlaceholder="Shelf product name"
          isSearchButtonDisabled={isLoading}
          onSearch={onSearch}
          onReset={onReset}
        />
        <TemplateTableStyled
          loading={isLoading}
          maxHeight="55rem"
          minHeight="55rem"
          showScrollShadows
          showRowHover
          totalElements={totalElements}
          pagination={{
            onPageChange: setPage,
            maxPage: totalPages || 1,
            tablePage: page,
          }}
          data-test-id={SHELF_PRODUCT_TEMPLATES_TABLE_TEST_IDS.TABLE}
        >
          <TableHeaderStyled data-test-id={SHELF_PRODUCT_TEMPLATES_TABLE_TEST_IDS.TABLE_HEADER}>
            <HeadingsRowStyled>
              <TableHeadingStyled
                width="4rem"
                sorting
                sortingKey={ProductTemplateSortBy.ID}
                onSortCallback={onTableSort}
              >
                IM ID
              </TableHeadingStyled>
              <TableHeadingStyled
                width="10rem"
                sorting
                sortingKey={ProductTemplateSortBy.NAME}
                onSortCallback={onTableSort}
              >
                Shelf Product Name
              </TableHeadingStyled>
              <TableHeadingStyled width="6rem">Product Line Name</TableHeadingStyled>
              <TableHeadingStyled width="8rem">Internal Product Name</TableHeadingStyled>
              <TableHeadingStyled width="8rem">External Product Name</TableHeadingStyled>
              <TableHeadingStyled width="6rem">Generated JSON</TableHeadingStyled>
              <TableHeadingStyled width="7rem" />
            </HeadingsRowStyled>
          </TableHeaderStyled>
          <TableBody>
            {shelfProductTemplates.map(({ product, productTemplate }) => (
              <TableRow
                key={product.imProductId}
                productId={product.imProductId}
                productLineName={product.productLine}
                shelfProductName={product.productDisplayName}
                templateId={productTemplate?.id}
                internalTemplateName={productTemplate?.internalName}
                externalTemplateName={productTemplate?.name}
              />
            ))}
          </TableBody>
        </TemplateTableStyled>
      </TableWrapperStyled>

      <UploadJsonModal uploadJsonModalDelegate={uploadJsonModalDelegate} onUploadJson={onUploadJson} />
      <ActionModal
        show={deleteTemplateModalDelegate.isVisible}
        title="Delete Product Template"
        subtitle={
          <>
            You are about to delete the product template for <b>{deleteTemplateModalDelegate.productName}</b>. This
            cannot be undone, but you can upload a new JSON anytime.
          </>
        }
        modalType={ModalType.DELETE}
        onClose={deleteTemplateModalDelegate.close}
        actionButtonText="Delete template"
        onAction={onDeleteTemplate}
        loading={deleteTemplateModalDelegate.isModalLoading}
        data-test-id={SHELF_PRODUCT_TEMPLATES_DELETE_TEMPLATE_MODAL_TEST_IDS.MODAL}
      />
    </ContentStyled>
  );
});

export default ShelfRFQProductTemplatesTable;
